import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Loading } from '@/isomorphic/components/loading/loading';
import { Button } from '@/isomorphic/components/typography/button/button';
import { useDataLayer } from '@/hooks/use-data-layer';
import { useGlobalQueries } from '@/hooks/use-global-queries';
import { useCompanyCreditCheck } from './use-company-credit-check';
import { StepOne } from './step-one';
import { StepTwo } from './step-two';

export const GA_TYPE = 'Company credit check';

const DEFAULT_PRODUCT_VARIANT = 'Company credit';

export type CompanyCreditCheckLabels = {
    // Step 1 — Credit Check
    stepOneTitle: string;
    stepOneDescription: string;
    stepOneInstruction: string;
    orgNumberLabel: string;
    orgNumberErrorText: string;
    loanAmountLabel: string;
    loanAmountErrorText: string;
    loanAmountMinErrorText: string;
    submitButtonText: string;
    disclaimer: string;

    // Loading
    loadingText: string;
    sendingText: string;

    // Step 2 — Contact Form (OK)
    successTitle: string;
    successDescription: string;
    nameLabel: string;
    nameErrorText: string;
    emailLabel: string;
    emailErrorText: string;
    phoneLabel: string;
    phoneErrorText: string;
    contactButtonText: string;

    // NOK — Contact Form
    nokTitle: string;
    nokDescription: string;
    nokContactButtonText: string;

    // Status messages
    errorTitle: string;
    errorDescription: string;
    backButtonText: string;

    // reCAPTCHA
    captchaErrorText: string;

    // Thank you
    thankYouTitle: string;
    thankYouDescription: string;
};

export type CompanyCreditCheckProps = {
    labels: CompanyCreditCheckLabels;
    align?: ContentAlign;
    minLoanAmount?: number;
    sourceFormName?: string;
    productVariant?: string;
    debugMode?: boolean;
    enableRecaptcha?: boolean;
    creditIndicationApiUrl?: string;
};

const DEFAULT_CREDIT_INDICATION_API_URL = 'https://api.credit-indication.sme-internetbank.dev.resurs.cloud/v1';

export type ContentAlign = 'left' | 'center';

export const CompanyCreditCheck = ({
    labels,
    align = 'left',
    minLoanAmount = 500000,
    sourceFormName = 'Credit Indication',
    productVariant = DEFAULT_PRODUCT_VARIANT,
    debugMode = false,
    enableRecaptcha = true,
    creditIndicationApiUrl = DEFAULT_CREDIT_INDICATION_API_URL,
}: CompanyCreditCheckProps) => {
    const dataLayerDefaults = useMemo(
        () => ({
            Product: 'Company credit',
            ProductVariant: productVariant,
        }),
        [productVariant]
    );
    const { dataLayerPush } = useDataLayer();
    const { pageQuery } = useGlobalQueries();
    const pageTitle = pageQuery.data?.page_title || pageQuery.data?.post_title || '';
    const { executeRecaptcha } = useGoogleReCaptcha();
    const [captchaError, setCaptchaError] = useState(false);
    const {
        step,
        fields,
        isLoading,
        creditStatus,
        contactError,
        contactFieldErrors,
        isSubmittingContact,
        updateField,
        checkCredit,
        submitContact,
        resetForm,
        _dev,
    } = useCompanyCreditCheck({ enableRecaptcha, creditIndicationApiUrl });

    // Fire once when the form page is first loaded
    useEffect(() => {
        dataLayerPush(
            {
                ...dataLayerDefaults,
                event: 'ApplicationStart',
                StepNumber: '0',
                StepName: 'Credit check form',
            },
            { businessVertical: 'B2B' }
        );
    }, []);

    const handleCreditCheck = async () => {
        let token: string | undefined;

        if (enableRecaptcha) {
            setCaptchaError(false);

            if (!executeRecaptcha) {
                setCaptchaError(true);
                return;
            }

            token = await executeRecaptcha();
            if (!token) {
                setCaptchaError(true);
                return;
            }
        }

        const status = await checkCredit(fields.orgNumber, token);

        if (status) {
            dataLayerPush(
                {
                    ...dataLayerDefaults,
                    event: 'ApplicationProgress',
                    StepNumber: '1',
                    StepName: 'Credit check result',
                    Status: status,
                },
                { businessVertical: 'B2B' }
            );
        }
    };

    const handleContactSubmit = async () => {
        let token: string | undefined;

        if (enableRecaptcha) {
            setCaptchaError(false);

            if (!executeRecaptcha) {
                setCaptchaError(true);
                return;
            }

            token = await executeRecaptcha();
            if (!token) {
                setCaptchaError(true);
                return;
            }
        }

        const loanAmount = Number(fields.loanAmount.replace(/\D/g, ''));
        const source = pageTitle ? `${pageTitle} - ${sourceFormName}` : sourceFormName;

        const status = await submitContact(
            fields.orgNumber,
            {
                name: fields.name,
                email: fields.email,
                telephone: fields.telephone,
                loanAmount,
                source,
                ...(fields.message ? { message: fields.message } : {}),
                ...(fields.annualRevenue ? { annualRevenue: fields.annualRevenue } : {}),
            },
            token
        );

        dataLayerPush(
            {
                ...dataLayerDefaults,
                event: 'ApplicationSubmitted',
                StepNumber: '2',
                StepName: 'Contact form submitted',
                Status: status,
            },
            { businessVertical: 'B2B' }
        );
    };

    const renderContent = () => {
        // Loading state — shown during credit check polling
        if (isLoading) {
            return (
                <div className="r-my-auto r-flex r-flex-col r-items-center r-justify-center r-gap-4 r-py-16">
                    <Loading />
                    <p className="r-text-base r-text-gray-500">{labels.loadingText}</p>
                </div>
            );
        }

        // Step 3 — Thank you
        if (step === 3) {
            return (
                <div className="r-my-auto r-flex r-flex-col r-gap-6">
                    <div className="r-flex r-flex-col r-items-center r-space-y-2 r-text-center">
                        <div className="r-mb-4 r-mt-2 r-flex r-h-20 r-w-20 r-items-center r-justify-center r-rounded-full r-bg-success">
                            <img src="/assets/icons/check.svg" className="r-w-10" />
                        </div>
                        <h3 className="r-mb-2 r-text-2xl r-font-medium r-text-gray-900">{labels.thankYouTitle}</h3>
                        <p className="r-text-base r-text-gray-700">{labels.thankYouDescription}</p>
                    </div>
                    <Button type="secondary" className="!r-mx-auto r-w-fit" onClick={resetForm}>
                        {labels.backButtonText}
                    </Button>
                </div>
            );
        }

        // Step 2 — NOK (credit check failed — show contact form with info)
        if (step === 2 && creditStatus === 'NOK') {
            return (
                <StepTwo
                    labels={labels}
                    align={align}
                    fields={fields}
                    updateField={updateField}
                    onSubmit={handleContactSubmit}
                    isSubmitting={isSubmittingContact}
                    contactError={contactError}
                    contactFieldErrors={contactFieldErrors}
                    variant="nok"
                    captchaError={captchaError}
                    enableRecaptcha={enableRecaptcha}
                />
            );
        }

        // Step 2 — ERROR
        if (step === 2 && creditStatus === 'ERROR') {
            return (
                <div className="r-my-auto r-flex r-flex-col r-gap-6">
                    <div className="r-flex r-flex-col r-items-center r-space-y-2 r-text-center">
                        <div className="r-mb-4 r-mt-2 r-flex r-h-20 r-w-20 r-items-center r-justify-center r-rounded-full r-bg-warning">
                            <img src="/assets/icons/close-cross.svg" className="r-w-10" />
                        </div>
                        <h3 className="r-mb-2 r-text-2xl r-font-medium r-text-gray-900">{labels.errorTitle}</h3>
                        <p className="r-text-base r-text-gray-700">{labels.errorDescription}</p>
                    </div>
                    <Button type="secondary" className="!r-mx-auto r-w-fit" onClick={resetForm}>
                        {labels.backButtonText}
                    </Button>
                </div>
            );
        }

        // Step 2 — OK (show contact form)
        if (step === 2 && creditStatus === 'OK') {
            return (
                <StepTwo
                    labels={labels}
                    align={align}
                    fields={fields}
                    updateField={updateField}
                    onSubmit={handleContactSubmit}
                    isSubmitting={isSubmittingContact}
                    contactError={contactError}
                    contactFieldErrors={contactFieldErrors}
                    captchaError={captchaError}
                    enableRecaptcha={enableRecaptcha}
                />
            );
        }

        // Step 1 — Credit check form (default)
        return (
            <StepOne
                labels={labels}
                align={align}
                fields={fields}
                updateField={updateField}
                onSubmit={handleCreditCheck}
                minLoanAmount={minLoanAmount}
                captchaError={captchaError}
                enableRecaptcha={enableRecaptcha}
                debugMode={debugMode}
            />
        );
    };

    // Track the tallest content height to prevent layout jumps between views
    const contentRef = useRef<HTMLDivElement>(null);
    const maxHeightRef = useRef(0);
    const [minHeight, setMinHeight] = useState(0);

    const updateMinHeight = useCallback(() => {
        if (contentRef.current) {
            const currentHeight = contentRef.current.scrollHeight;
            if (currentHeight > maxHeightRef.current) {
                maxHeightRef.current = currentHeight;
                setMinHeight(currentHeight);
            }
        }
    }, []);

    useEffect(() => {
        if (!contentRef.current) return;

        const observer = new ResizeObserver(updateMinHeight);
        observer.observe(contentRef.current);

        return () => observer.disconnect();
    }, [updateMinHeight]);

    const devButtons: { label: string; action: () => void }[] = [
        {
            label: 'Step 1 — Form',
            action: () => {
                resetForm();
            },
        },
        {
            label: 'Loading (credit check)',
            action: () => {
                _dev.setIsLoading(true);
                _dev.setCreditStatus(null);
                _dev.setIsSubmittingContact(false);
            },
        },
        {
            label: 'Step 2 — OK (contact form)',
            action: () => {
                _dev.setIsLoading(false);
                _dev.setIsSubmittingContact(false);
                _dev.setCreditStatus('OK');
                _dev.setStep(2);
            },
        },
        {
            label: 'Step 2 — NOK (contact form)',
            action: () => {
                _dev.setIsLoading(false);
                _dev.setIsSubmittingContact(false);
                _dev.setCreditStatus('NOK');
                _dev.setStep(2);
            },
        },
        {
            label: 'Step 2 — ERROR',
            action: () => {
                _dev.setIsLoading(false);
                _dev.setIsSubmittingContact(false);
                _dev.setCreditStatus('ERROR');
                _dev.setStep(2);
            },
        },
        {
            label: 'Sending (contact)',
            action: () => {
                _dev.setIsLoading(false);
                _dev.setIsSubmittingContact(true);
                _dev.setCreditStatus('OK');
                _dev.setStep(2);
            },
        },
        {
            label: 'Step 3 — Thank you',
            action: () => {
                _dev.setIsLoading(false);
                _dev.setIsSubmittingContact(false);
                _dev.setStep(3);
            },
        },
    ];

    return (
        <div className="r-max-w-lg">
            <div ref={contentRef} className="r-flex r-flex-col" style={{ minHeight: minHeight > 0 ? `${minHeight}px` : undefined }}>
                {renderContent()}
            </div>

            {debugMode && (
                <div className="r-mt-8 r-rounded-xl r-border-2 r-border-dashed r-border-amber-400 r-bg-amber-50 r-p-4">
                    <p className="r-mb-3 r-text-xs r-font-bold r-uppercase r-tracking-wide r-text-amber-700">Dev: Jump to view</p>
                    <div className="r-flex r-flex-wrap r-gap-2">
                        {devButtons.map(({ label, action }) => (
                            <button
                                key={label}
                                onClick={action}
                                className="r-rounded-lg r-border r-border-amber-300 r-bg-white r-px-3 r-py-1.5 r-text-xs r-font-medium r-text-amber-800 hover:r-bg-amber-100"
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};
